/**
 * Nocwell · PDP bundle promo (Sleep Set)
 *
 * Drives the bundle card that sits under the colour picker on both product pages.
 *
 *  - The half of the set the shopper is already looking at follows the page's own colour
 *    picker (`shopify:product:select`), so the card never argues with the variant above it.
 *  - The other half is chosen inside the card.
 *  - Adding the set also removes any standalone mask / earplugs already in the cart, so the
 *    shopper pays the bundle price instead of the sum of the parts.
 *
 * Written without private class fields on purpose: the Shopify CDN polyfills theme JS down to
 * ES5 and chokes on `#private` members, which would leave the whole bundle unparsed.
 */
class NcPdpBundle extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.added = false;
    this.role = 'mask';
    this.state = { maskColor: '', plugColor: '' };
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onProductSelect = this.handleProductSelect.bind(this);
  }

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.role = this.dataset.role === 'plug' ? 'plug' : 'mask';

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // Capture phase: the event is dispatched from the variant picker, and capture works no
    // matter whether it bubbles.
    document.addEventListener('shopify:product:select', this.onProductSelect, true);

    this.applyDefaults();
    this.prewarm();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:product:select', this.onProductSelect, true);
  }

  variants() {
    try {
      const parsed = JSON.parse(this.dataset.variants || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /** Colour name -> product shot, so the thumbnails can follow the selection. */
  images(group) {
    try {
      const raw = group === 'mask' ? this.dataset.maskImages : this.dataset.plugImages;
      const parsed = JSON.parse(raw || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  /** Swaps a thumbnail to the shot that matches the colour currently selected for that half. */
  syncThumb(group) {
    const color = group === 'mask' ? this.state.maskColor : this.state.plugColor;
    const url = this.images(group)[color];
    if (!url) return;

    const img = this.querySelector('[data-nc-thumb="' + group + '"]');
    if (!img || img.getAttribute('src') === url) return;

    // Swap only once the new shot is decoded, so the thumbnail never flashes empty; the
    // state guard keeps a slow image from overwriting a newer selection.
    const preload = new Image();
    preload.onload = () => {
      const current = group === 'mask' ? this.state.maskColor : this.state.plugColor;
      if (current === color) img.setAttribute('src', url);
    };
    preload.src = url;
  }

  /** Fetches every colour shot up front so the first swap is instant. */
  prewarm() {
    ['mask', 'plug'].forEach((group) => {
      const dict = this.images(group);
      Object.keys(dict).forEach((key) => {
        (new Image()).src = dict[key];
      });
    });
  }

  /** Seed the colours: the page's own colour first, then the first sellable combination. */
  applyDefaults() {
    const list = this.variants();
    const first = list.find((v) => v.available) || list[0] || {};
    const pageColor = this.dataset.pageColor || '';

    if (this.role === 'mask') {
      this.state.maskColor = pageColor || first.o1 || '';
      this.state.plugColor = first.o2 || '';
    } else {
      this.state.plugColor = pageColor || first.o2 || '';
      this.state.maskColor = first.o1 || '';
    }

    this.sync();
  }

  /** Keeps the bundle in step with the colour picker above the card. */
  handleProductSelect(event) {
    const pageHandle = this.dataset.pageHandle || '';
    const product = (event && event.product) || {};
    if (pageHandle && product.handle && product.handle !== pageHandle) return;

    const options = (event && event.selectedOptions) || [];
    const picked = options.find((option) => /colou?r/i.test(option.name || ''));
    if (!picked || !picked.value) return;

    if (this.role === 'mask') this.state.maskColor = picked.value;
    else this.state.plugColor = picked.value;

    this.sync();
  }

  handleKeydown(event) {
    const swatch = event.target.closest('[data-nc-color]');
    if (!swatch) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      swatch.click();
    }
  }

  handleClick(event) {
    const swatch = event.target.closest('[data-nc-color]');
    if (swatch) {
      event.preventDefault();
      this.state[swatch.dataset.ncColorGroup === 'mask' ? 'maskColor' : 'plugColor'] = swatch.dataset.ncColor;
      this.sync();
      return;
    }

    const button = event.target.closest('[data-nc-bundle-button]');
    if (button) {
      event.preventDefault();
      if (!this.added) this.addSet(button);
    }
  }

  /** The set variant matching the two colours currently selected. */
  resolveVariant() {
    const list = this.variants();
    const maskColor = this.state.maskColor;
    const plugColor = this.state.plugColor;
    return (
      list.find((v) => v.o1 === maskColor && v.o2 === plugColor && v.available) ||
      list.find((v) => v.o1 === maskColor && v.o2 === plugColor) ||
      null
    );
  }

  sync() {
    this.syncThumb('mask');
    this.syncThumb('plug');

    this.querySelectorAll('[data-nc-color]').forEach((swatch) => {
      const group = swatch.dataset.ncColorGroup === 'mask' ? 'maskColor' : 'plugColor';
      const active = swatch.dataset.ncColor === this.state[group];
      swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
      swatch.classList.toggle('is-active', active);
      if (active) {
        const nameNode = this.querySelector('[data-nc-bundle-color-name="' + swatch.dataset.ncColorGroup + '"]');
        if (nameNode) nameNode.textContent = swatch.dataset.ncColor;
      }
    });

    const selected = this.resolveVariant();
    const button = this.querySelector('[data-nc-bundle-button]');
    if (!button || this.added) return;

    button.disabled = !selected;
    const label = button.querySelector('[data-nc-bundle-label]');
    if (label) label.textContent = selected ? this.dataset.labelReady : this.dataset.labelUnavailable;
    this.message('', false);
  }

  message(text, isError) {
    const node = this.querySelector('[data-nc-bundle-message]');
    if (!node) return;
    node.textContent = text || '';
    node.hidden = !text;
    node.classList.toggle('nc-bundle__message--error', Boolean(isError));
  }

  async addSet(button) {
    if (this.busy) return;
    const variant = this.resolveVariant();
    if (!variant) return;

    this.busy = true;
    button.classList.add('is-loading');
    button.disabled = true;
    this.message('', false);

    try {
      const cart = await this.getCart();
      const removeIds = [this.dataset.maskProductId, this.dataset.plugProductId].filter(Boolean);
      const lines = [{ merchandiseId: variant.id, quantity: 1 }];

      for (const item of cart.items || []) {
        if (removeIds.indexOf(String(item.product_id)) !== -1) {
          lines.push({ id: item.key, quantity: 0 });
        }
      }

      let applied = false;

      if (window.Shopify && window.Shopify.actions && window.Shopify.actions.updateCart) {
        try {
          const result = await window.Shopify.actions.updateCart({ lines });
          const errors = (result && result.userErrors) || [];
          if (errors.length) throw new Error(errors[0].message);
          applied = true;
        } catch (error) {
          console.warn('[nc-pdp-bundle] standard cart action failed, falling back to the AJAX API:', error);
        }
      }

      if (!applied) await this.ajax(lines, cart);

      this.added = true;
      const label = button.querySelector('[data-nc-bundle-label]');
      if (label) label.textContent = this.dataset.labelAdded || 'Added to cart';
      button.disabled = true;
      button.classList.remove('is-loading');

      if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
        window.Shopify.actions.openCart().catch(() => {});
      }
    } catch (error) {
      this.message(this.dataset.messageError || (error && error.message) || 'Something went wrong.', true);
      button.disabled = false;
      button.classList.remove('is-loading');
    } finally {
      this.busy = false;
    }
  }

  async getCart() {
    const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    const response = await fetch(base + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not read the cart.');
    return response.json();
  }

  /** Fallback for when the standard cart action is not on the page. */
  async ajax(lines, cart) {
    const routes = (window.Theme && window.Theme.routes) || {};
    const add = lines.find((line) => line.merchandiseId);
    await fetch((routes.cart_add_url || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: add.merchandiseId, quantity: add.quantity }] }),
    });

    for (const item of cart.items || []) {
      const shouldRemove = lines.some((line) => line.id === item.key);
      if (!shouldRemove) continue;
      await fetch((routes.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
    }
    window.location.reload();
  }
}

if (!customElements.get('nc-pdp-bundle')) {
  customElements.define('nc-pdp-bundle', NcPdpBundle);
}
